
/** @jsxImportSource react */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from '../components/ui/Toast';
import { useAuth } from '../contexts/auth'
import { useTranslation } from '../i18n'
import { api } from '../services/api'
import { useMutation } from '@tanstack/react-query';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  ArrowDown,
  ArrowUp,
  BarChart2,
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  DollarSign,
  Package,
  PlusCircle,
  Users,
  AlertTriangle,
} from 'lucide-react'
import { Order, OrderStatus, UserRole, User } from '../types'
import { Modal } from '../components/ui/Modal'
import NewOrderPage from './NewOrderPage'

// Utility functions
const DashboardCard: React.FC<{ children: React.ReactNode; className?: string; onClick?: () => void; }> = ({ children, className = '', onClick }) => (
  <div
    className={`bg-white rounded-xl border border-secondary-200/70 shadow-sm hover:shadow-md transition-all duration-300 ${className}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : -1}
  >
    {children}
  </div>
)

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: boolean;
  percentage?: number;
  subtitle?: string;
}> = ({
  title,
  value,
  icon,
  color,
  trend,
  percentage,
  subtitle,
}) => {
  const { formatNumber } = useTranslation();
  return (
  <DashboardCard className="relative overflow-hidden group animate-fade-in">
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-secondary-50/30 opacity-80"></div>
    <div className="absolute top-0 end-0 w-32 h-32 -mt-8 -me-8 bg-gradient-to-bl from-primary-100/30 to-transparent rounded-full"></div>
    <div className="relative z-10 p-6">
      <div className="flex justify-between items-start mb-4">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${color} shadow-sm transform transition-transform group-hover:scale-110 duration-300`}
        >
          {icon}
        </div>
        {trend && percentage != null && (
          <div
            className={`flex items-center text-sm font-medium ${percentage >= 0 ? 'text-primary-600' : 'text-secondary-600'}`}
          >
            {percentage >= 0 ? (
              <ArrowUp className="w-3.5 h-3.5 me-1" />
            ) : (
              <ArrowDown className="w-3.5 h-3.5 me-1" />
            )}
            {formatNumber(Math.abs(percentage))}%
          </div>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-secondary-500 mb-1">{title}</h3>
        <p className="text-2xl font-bold text-secondary-900 mb-1 transition-all duration-300 group-hover:text-primary-600">
          {value}
        </p>
        {subtitle && <p className="text-xs text-secondary-500">{subtitle}</p>}
      </div>
    </div>
  </DashboardCard>
)};

const StatCardSkeleton = () => (
  <DashboardCard className="p-6 animate-pulse">
    <div className="flex justify-between items-start mb-4">
      <div className="w-12 h-12 bg-secondary-200 rounded-lg"></div>
      <div className="w-16 h-6 bg-secondary-200 rounded"></div>
    </div>
    <div>
      <div className="h-4 w-24 bg-secondary-200 rounded mb-2"></div>
      <div className="h-8 w-20 bg-secondary-200 rounded"></div>
    </div>
  </DashboardCard>
)

const ChartCard: React.FC<{
  title: string;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
  subtitle?: string;
}> = ({
  title,
  children,
  className = '',
  action = null,
  subtitle = null,
}) => (
  <DashboardCard className={`p-6 ${className} animate-fade-in`}>
    <div className="flex justify-between items-center mb-2">
      <div>
        <h3 className="text-lg font-semibold text-secondary-900">{title}</h3>
        {subtitle && <p className="text-sm text-secondary-500">{subtitle}</p>}
      </div>
      {action}
    </div>
    <div className="mt-4">{children}</div>
  </DashboardCard>
)

const ChartCardSkeleton: React.FC<{ height?: string, className?: string }> = ({ height = 'h-80', className='' }) => (
  <DashboardCard className={`p-6 animate-pulse ${className}`}>
    <div className="flex justify-between items-center mb-6">
      <div className="h-6 w-48 bg-secondary-200 rounded"></div>
      <div className="h-8 w-24 bg-secondary-200 rounded"></div>
    </div>
    <div className={`${height} bg-secondary-200 rounded`}></div>
  </DashboardCard>
)

// Admin & Staff Dashboard Component
const InternalDashboard: React.FC<{ orders: Order[], users: User[] }> = ({ orders, users }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { t, formatNumber, formatCurrency, formatDate } = useTranslation();
  const [dateFilter, setDateFilter] = React.useState('today'); // today, week, month, year, custom
  const [customDateRange, setCustomDateRange] = React.useState({ start: '', end: '' });
  const [isDateFilterOpen, setIsDateFilterOpen] = React.useState(false);
  const dateFilterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (dateFilterRef.current && !dateFilterRef.current.contains(event.target as Node)) {
            setIsDateFilterOpen(false);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { startDate, endDate } = React.useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date();
    end.setHours(23, 59, 59, 999);

    switch (dateFilter) {
        case 'today':
            start.setHours(0, 0, 0, 0);
            break;
        case 'week':
            start.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1) ); // Assuming week starts on Monday
            start.setHours(0, 0, 0, 0);
            break;
        case 'month':
            start = new Date(today.getFullYear(), today.getMonth(), 1);
            break;
        case 'year':
            start = new Date(today.getFullYear(), 0, 1);
            break;
        case 'custom':
            if (customDateRange.start && customDateRange.end) {
                start = new Date(customDateRange.start);
                start.setHours(0, 0, 0, 0);
                end = new Date(customDateRange.end);
                end.setHours(23, 59, 59, 999);
            } else {
                start.setHours(0, 0, 0, 0);
            }
            break;
    }
    return { startDate: start, endDate: end };
  }, [dateFilter, customDateRange]);

  const { prevStartDate, prevEndDate } = React.useMemo(() => {
    if (!startDate || !endDate) return { prevStartDate: null, prevEndDate: null };
    const diff = endDate.getTime() - startDate.getTime();
    const prevEnd = new Date(startDate.getTime() - 1);
    const prevStart = new Date(prevEnd.getTime() - diff);
    return { prevStartDate: prevStart, prevEndDate: prevEnd };
  }, [startDate, endDate]);
  
  const filteredOrders = React.useMemo(() => {
    if (!startDate || !endDate) return orders;
    return orders.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= startDate && orderDate <= endDate;
    });
  }, [orders, startDate, endDate]);

  const prevFilteredOrders = React.useMemo(() => {
    if (!prevStartDate || !prevEndDate) return [];
    return orders.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= prevStartDate && orderDate <= prevEndDate;
    });
  }, [orders, prevStartDate, prevEndDate]);

  const kpis = React.useMemo(() => {
    // Current period calculations
    const totalOrders = filteredOrders.length;
    const inProgress = filteredOrders.filter(o => ![OrderStatus.Delivered, OrderStatus.Cancelled].includes(o.status)).length;
    const completed = filteredOrders.filter(o => o.status === OrderStatus.Delivered).length;
    const totalClients = users.filter((u) => u.role === UserRole.Client).length; // This is not date-filtered.
    const totalRevenue = filteredOrders.filter((o) => o.finalPrice && o.paymentStatus === 'Verified').reduce((sum, o) => sum + (o.finalPrice || 0), 0);

    // Previous period calculations for trend
    const prevTotalOrders = prevFilteredOrders.length;
    const prevInProgress = prevFilteredOrders.filter(o => ![OrderStatus.Delivered, OrderStatus.Cancelled].includes(o.status)).length;
    const prevCompleted = prevFilteredOrders.filter(o => o.status === OrderStatus.Delivered).length;
    const prevTotalRevenue = prevFilteredOrders.filter((o) => o.finalPrice && o.paymentStatus === 'Verified').reduce((sum, o) => sum + (o.finalPrice || 0), 0);
    
    const calculateGrowth = (current: number, previous: number) => {
        if (previous > 0) return Math.round(((current - previous) / previous) * 100);
        return current > 0 ? 100 : 0;
    };
    
    return {
        totalOrders,
        inProgress,
        totalClients,
        totalRevenue,
        completed,
        orderGrowth: calculateGrowth(totalOrders, prevTotalOrders),
        revenueGrowth: calculateGrowth(totalRevenue, prevTotalRevenue),
        inProgressGrowth: calculateGrowth(inProgress, prevInProgress),
        completedGrowth: calculateGrowth(completed, prevCompleted),
    };
  }, [filteredOrders, prevFilteredOrders, users]);

  const chartData = React.useMemo(() => {
    if (filteredOrders.length === 0) return [];
    const diffDays = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);

    if (diffDays <= 2) { // Group by hour
      const hoursMap: Map<number, { orders: number; revenue: number }> = new Map();
      for (let i = 0; i < 24; i++) {
        hoursMap.set(i, { orders: 0, revenue: 0 });
      }

      filteredOrders.forEach((order) => {
        const hour = new Date(order.createdAt).getUTCHours();
        const currentData = hoursMap.get(hour);
        if (currentData) {
          currentData.orders += 1;
          if (order.finalPrice && order.paymentStatus === 'Verified') {
            currentData.revenue += order.finalPrice;
          }
        }
      });

      return Array.from(hoursMap.entries()).map(([hour, data]) => {
        const d = new Date();
        d.setUTCHours(hour, 0, 0, 0);
        return {
          name: formatDate(d, { hour: 'numeric', timeZone: 'UTC' }),
          ...data,
        };
      });
    } else if (diffDays <= 90) { // Group by day
      const daysMap: Map<string, { orders: number; revenue: number }> = new Map();
      const currentDate = new Date(startDate);
      currentDate.setUTCHours(0, 0, 0, 0);
      const endDateCopy = new Date(endDate);
      endDateCopy.setUTCHours(0, 0, 0, 0);

      while (currentDate <= endDateCopy) {
        const dayKey = formatDate(currentDate, { month: 'short', day: 'numeric', timeZone: 'UTC' });
        daysMap.set(dayKey, { orders: 0, revenue: 0 });
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
      }

      filteredOrders.forEach((order) => {
        const dayKey = formatDate(order.createdAt, { month: 'short', day: 'numeric', timeZone: 'UTC' });
        const currentData = daysMap.get(dayKey);
        if (currentData) {
          currentData.orders += 1;
          if (order.finalPrice && order.paymentStatus === 'Verified') {
            currentData.revenue += order.finalPrice;
          }
        }
      });

      return Array.from(daysMap.entries()).map(([name, values]) => ({ name, ...values }));
    } else { // Group by month
      const monthsMap: Map<string, { orders: number; revenue: number }> = new Map();
      const currentDate = new Date(startDate);
      currentDate.setUTCDate(1);
      currentDate.setUTCHours(0, 0, 0, 0);
      const endDateCopy = new Date(endDate);

      while (currentDate <= endDateCopy) {
        const monthKey = formatDate(currentDate, { month: 'short', year: 'numeric', timeZone: 'UTC' });
        monthsMap.set(monthKey, { orders: 0, revenue: 0 });
        currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
      }

      filteredOrders.forEach((order) => {
        const monthKey = formatDate(order.createdAt, { month: 'short', year: 'numeric', timeZone: 'UTC' });
        const currentData = monthsMap.get(monthKey);
        if (currentData) {
          currentData.orders += 1;
          if (order.finalPrice && order.paymentStatus === 'Verified') {
            currentData.revenue += order.finalPrice;
          }
        }
      });

      return Array.from(monthsMap.entries()).map(([name, values]) => ({ name, ...values }));
    }
  }, [filteredOrders, startDate, endDate, formatDate]);

  const dateFilterOptions = {
    today: t('today'),
    week: t('thisWeek'),
    month: t('thisMonth'),
    year: t('thisYear'),
    custom: t('customRange'),
  };

  return (
    <div>
      <div className="relative z-20 mb-8 bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-secondary-200/70 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-secondary-900 mb-1">{user?.role === UserRole.Admin ? t('adminDashboard') : t('staffDashboard')}</h1>
            <p className="text-secondary-600">{formatDate(new Date(), { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-wrap items-center justify-end gap-3">
            <div ref={dateFilterRef} className="relative">
                <button onClick={() => setIsDateFilterOpen(v => !v)} className="flex items-center gap-2 px-3 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-colors shadow-sm">
                    <Calendar className="w-4 h-4" />
                    <span>{dateFilterOptions[dateFilter]}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isDateFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateFilterOpen && (
                    <div className="absolute top-full end-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-50 animate-fade-in">
                        {Object.entries(dateFilterOptions).map(([key, label]) => (
                            <button key={key} onClick={() => { setDateFilter(key); setIsDateFilterOpen(false); }} className="block w-full text-start px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-100">{label}</button>
                        ))}
                    </div>
                )}
            </div>
            {dateFilter === 'custom' && (
                <div className="flex flex-wrap items-center gap-2 animate-fade-in">
                    <input type="date" value={customDateRange.start} onChange={e => setCustomDateRange(prev => ({...prev, start: e.target.value}))} className="px-2 py-1.5 border border-secondary-200 rounded-lg text-sm" />
                    <input type="date" value={customDateRange.end} onChange={e => setCustomDateRange(prev => ({...prev, end: e.target.value}))} className="px-2 py-1.5 border border-secondary-200 rounded-lg text-sm" />
                </div>
            )}
            {user?.role === UserRole.Admin && (
              <button onClick={() => navigate('/reports')} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-colors shadow-sm">
                <BarChart2 className="w-4 h-4" /><span>{t('reports')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title={t('totalOrders')} value={formatNumber(kpis.totalOrders)} icon={<Package className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-primary-500 to-primary-600" trend percentage={kpis.orderGrowth} subtitle={t('vsPreviousPeriod')} />
        <StatCard title={t('inProgress')} value={formatNumber(kpis.inProgress)} icon={<Clock className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-secondary-500 to-secondary-600" trend percentage={kpis.inProgressGrowth} subtitle={t('vsPreviousPeriod')} />
        {user?.role === UserRole.Admin ? (
          <StatCard title={t('totalClients')} value={formatNumber(kpis.totalClients)} icon={<Users className="h-6 w-6 text-white" />} color={'bg-gradient-to-br from-primary-600 to-primary-700'} subtitle={t('activeUsers')} />
        ) : (
          <StatCard title={t('completed')} value={formatNumber(kpis.completed)} icon={<CheckCircle className="h-6 w-6 text-white" />} color={'bg-gradient-to-br from-primary-700 to-primary-800'} trend percentage={kpis.completedGrowth} subtitle={t('vsPreviousPeriod')} />
        )}
        <StatCard title={t('totalRevenue')} value={formatCurrency(kpis.totalRevenue)} icon={<DollarSign className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-primary-800 to-primary-900" trend percentage={kpis.revenueGrowth} subtitle={t('vsPreviousPeriod')} />
      </div>
      {user?.role === UserRole.Admin && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <ChartCard title={t('orders')} subtitle={dateFilterOptions[dateFilter]} className="lg:col-span-3">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#203946" stopOpacity={0.3} /><stop offset="95%" stopColor="#203946" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => formatNumber(value as number)} />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} formatter={(value: number) => [formatNumber(value), t('orders')]} />
                  <Area type="monotone" dataKey="orders" stroke="#203946" strokeWidth={3} fillOpacity={1} fill="url(#colorOrders)" name={t('orders')} activeDot={{ r: 6, strokeWidth: 0 }} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
          <div className="mb-8">
            <ChartCard title={t('revenue')} subtitle={dateFilterOptions[dateFilter]}>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs><linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#334155" stopOpacity={0.3} /><stop offset="95%" stopColor="#334155" stopOpacity={0} /></linearGradient></defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(value) => formatCurrency(value as number, { notation: 'compact' })} />
                  <Tooltip formatter={(value: number) => [formatCurrency(value), t('revenue')]} contentStyle={{ backgroundColor: 'white', border: 'none', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="revenue" stroke="#334155" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" name={t('revenue')} activeDot={{ r: 6, strokeWidth: 0 }} animationDuration={1000} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}
    </div>
  )
}

// Client Dashboard Component
const ClientDashboard: React.FC<{ orders: Order[] }> = ({ orders }) => {
  const navigate = useNavigate()
  const { user } = useAuth();
  const { t, formatNumber, formatCurrency } = useTranslation();
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  
  const resendMutation = useMutation({
    mutationFn: () => api.resendVerificationEmail(),
    onSuccess: () => {
        toast.success(t('verificationEmailSent'));
    },
    onError: (error: any) => {
        let message = t('unexpectedError');
        if (error.code === 'auth/too-many-requests') {
            message = t('tooManyRequestsError') || 'Too many requests. Please try again later.';
        } else if (error.message) {
            message = error.message;
        }
        toast.error(message);
    }
  });

  const handleResendVerification = () => {
      if (resendMutation.isPending) return;
      resendMutation.mutate();
  };

  const stats = React.useMemo(() => ({ 
    totalOrders: orders.length, 
    inProgress: orders.filter(o => ![OrderStatus.Delivered, OrderStatus.Cancelled].includes(o.status)).length, 
    completed: orders.filter(o => o.status === OrderStatus.Delivered).length, 
    totalSpent: orders.filter(o => o.finalPrice && o.status !== OrderStatus.Cancelled).reduce((sum: number, o) => sum + (o.finalPrice || 0), 0) 
  }), [orders])
  
  return (
    <div>
      {user && !user.isEmailVerified && (
        <div className="mb-8 bg-secondary-100 p-4 border-s-4 border-secondary-400 rounded-e-lg animate-fade-in">
            <div className="flex">
                <div className="flex-shrink-0">
                    <AlertTriangle className="h-5 w-5 text-secondary-500" />
                </div>
                <div className="ms-3">
                    <p className="text-sm text-secondary-800">
                        {t('verifyEmailPrompt')}{' '}
                        <button onClick={handleResendVerification} disabled={resendMutation.isPending} className="font-semibold underline hover:text-secondary-900 disabled:opacity-70 disabled:cursor-wait">
                            {resendMutation.isPending ? t('sending') : t('resendVerificationEmail')}
                        </button>
                    </p>
                </div>
            </div>
        </div>
      )}
      <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-secondary-200/70 relative overflow-hidden animate-fade-in">
        <div className="absolute top-0 end-0 w-64 h-64 -mt-10 -me-10 bg-gradient-to-bl from-primary-100/40 to-transparent rounded-full"></div>
        <div className="relative z-10">
          <h1 className="text-2xl font-bold text-secondary-900 mb-1">{t('welcomeBack')} {user?.name}!</h1>
          <p className="text-secondary-600 max-w-2xl">{t('clientDashboardWelcome')}</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <button onClick={() => setIsNewOrderModalOpen(true)} className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg text-sm font-medium hover:from-primary-700 hover:to-primary-600 transition-colors shadow-sm"><PlusCircle className="w-4 h-4 me-2" />{t('newOrder')}</button>
            <button onClick={() => navigate('/orders')} className="inline-flex items-center px-4 py-2 bg-white border border-secondary-200 rounded-lg text-sm font-medium text-secondary-600 hover:bg-secondary-50 transition-colors shadow-sm"><Package className="w-4 h-4 me-2" />{t('viewAllOrders')}</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard title={t('totalOrders')} value={formatNumber(stats.totalOrders)} icon={<Package className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-primary-500 to-primary-600" subtitle={t('allTime')} />
        <StatCard title={t('inProgress')} value={formatNumber(stats.inProgress)} icon={<Clock className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-secondary-500 to-secondary-600" subtitle={t('activeOrders')} />
        <StatCard title={t('completed')} value={formatNumber(stats.completed)} icon={<CheckCircle className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-primary-700 to-primary-800" subtitle={t('deliveredOrders')} />
        <StatCard title={t('totalSpent')} value={formatCurrency(stats.totalSpent)} icon={<DollarSign className="h-6 w-6 text-white" />} color="bg-gradient-to-br from-primary-800 to-primary-900" subtitle={t('allTime')} />
      </div>
      
      {isNewOrderModalOpen && <Modal size="3xl" title={t('createNewOrder')} onClose={() => setIsNewOrderModalOpen(false)}><NewOrderPage onClose={() => setIsNewOrderModalOpen(false)} /></Modal>}
    </div>
  )
}

// Main Dashboard Component
const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const { t } = useTranslation()
  
  // Data Fetching with real-time listeners
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    
    // Initial loading state set only if we don't have data
    if (orders.length === 0) setIsLoading(true);

    // Use synchronous listenToOrders with direct user object
    const unsubOrders = api.listenToOrders(user, (newOrders) => {
        setOrders(newOrders);
        setIsLoading(false);
    });

    let unsubUsers: (() => void) | undefined;
    if (user.role === UserRole.Admin || user.role === UserRole.Staff) {
        unsubUsers = api.listenToUsers((newUsers) => {
            setUsers(newUsers);
        });
    }
    
    return () => {
        if (unsubOrders) unsubOrders();
        if (unsubUsers) unsubUsers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.role]);

  // Add null check for user
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-secondary-600">{t('loadingUserData')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {isLoading ? (
        <div>
          <div className="mb-8 bg-white p-6 rounded-xl shadow-sm border border-secondary-200/70 animate-pulse">
            <div className="h-8 w-48 bg-secondary-200 rounded mb-2"></div>
            <div className="h-4 w-64 bg-secondary-200 rounded"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton /><StatCardSkeleton />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <ChartCardSkeleton className="lg:col-span-2" /><ChartCardSkeleton />
          </div>
        </div>
      ) : (
        <>
          {user.role === UserRole.Client ? (
            <ClientDashboard orders={orders} />
          ) : [UserRole.Staff, UserRole.Admin].includes(user.role) ? (
            <InternalDashboard orders={orders} users={users} />
          ) : null}
        </>
      )}
    </div>
  )
}

export default DashboardPage